import { useRef, useState, type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ConfirmOptions = {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  requireReason?: { minLength?: number; label?: string; placeholder?: string };
};

type Pending = ConfirmOptions & {
  resolve: (value: { confirmed: boolean; reason?: string }) => void;
};

let openConfirmExternal:
  ((opts: ConfirmOptions) => Promise<{ confirmed: boolean; reason?: string }>) | null = null;

/** Imperative confirm — use from click handlers */
export function confirmAction(opts: ConfirmOptions) {
  if (!openConfirmExternal) {
    return Promise.resolve({ confirmed: false as const });
  }
  return openConfirmExternal(opts);
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState("");
  const settled = useRef(false);

  openConfirmExternal = (opts) =>
    new Promise((resolve) => {
      settled.current = false;
      setReason("");
      setPending({ ...opts, resolve });
    });

  const minLen = pending?.requireReason?.minLength ?? 5;
  const reasonOk = !pending?.requireReason || reason.trim().length >= minLen;

  function finish(confirmed: boolean) {
    if (settled.current) return;
    settled.current = true;
    const current = pending;
    setPending(null);
    setReason("");
    current?.resolve({
      confirmed,
      reason: current.requireReason ? reason.trim() : undefined,
    });
  }

  return (
    <>
      {children}
      <AlertDialog
        open={Boolean(pending)}
        onOpenChange={(open) => {
          if (!open) finish(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.title}</AlertDialogTitle>
            <AlertDialogDescription>{pending?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          {pending?.requireReason ? (
            <div className="space-y-2">
              <label className="text-xs font-semibold" htmlFor="confirm-reason">
                {pending.requireReason.label || "Reason (required)"}
              </label>
              <textarea
                id="confirm-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder={pending.requireReason.placeholder || "Explain why…"}
                className="w-full resize-none rounded-md border border-input bg-background p-3 text-sm"
              />
              {!reasonOk ? (
                <p className="text-[11px] text-muted-foreground">
                  Enter at least {minLen} characters.
                </p>
              ) : null}
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>{pending?.cancelLabel || "Cancel"}</AlertDialogCancel>
            <Button
              type="button"
              disabled={!reasonOk}
              variant={pending?.destructive ? "destructive" : "default"}
              className={cn(pending?.destructive && "bg-destructive text-destructive-foreground")}
              onClick={(e) => {
                e.preventDefault();
                finish(true);
              }}
            >
              {pending?.confirmLabel || "Confirm"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function ConfirmButton({
  options,
  onConfirm,
  children,
  className,
  variant,
  size,
  disabled,
}: {
  options: ConfirmOptions;
  onConfirm: (result: { reason?: string }) => void | Promise<void>;
  children: ReactNode;
  className?: string;
  variant?: "default" | "outline" | "ghost" | "destructive" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      className={className}
      variant={variant}
      size={size}
      disabled={disabled}
      onClick={async () => {
        const result = await confirmAction(options);
        if (result.confirmed) await onConfirm({ reason: result.reason });
      }}
    >
      {children}
    </Button>
  );
}
